import { Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { createStyles, makeStyles } from '@mui/styles';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ImportPage } from 'services/imports/fileToPages';
import { StatusChip, StatusTone } from 'views/imports/components/status-chip';
import type { OcrPageState } from 'views/imports/types';

type PageStatus = { labelKey: string; tone: StatusTone };

const resolveStatus = (extracted: boolean, ocr: OcrPageState | undefined): PageStatus => {
  if (extracted) return { labelKey: 'imports:pages.extracted', tone: 'success' };
  if (!ocr) return { labelKey: 'imports:pages.pending', tone: 'neutral' };
  switch (ocr.status) {
    case 'done':
      return { labelKey: 'imports:pages.done', tone: 'info' };
    case 'error':
      return { labelKey: 'imports:pages.error', tone: 'error' };
    case 'skipped':
      return { labelKey: 'imports:pages.skipped', tone: 'neutral' };
    case 'cancelled':
      return { labelKey: 'imports:pages.cancelled', tone: 'neutral' };
    default: {
      const unreachable: never = ocr.status;
      return unreachable;
    }
  }
};

/** Maps the English messages produced by `filesToPages` to i18n keys; null keeps the raw message. */
const failedMessageKey = (message: string): string | null => {
  if (message === 'Unsupported file type') return 'imports:dropzone.unsupported';
  if (message.includes('more than')) return 'imports:dropzone.tooManyPages';
  if (message.includes('too large')) return 'imports:dropzone.pageTooLarge';
  return null;
};

const useStyles = makeStyles((theme: Theme) => createStyles({
  meta: {
    flex: 1,
    minWidth: 0,
  },
  muted: {
    color: theme.palette.text.secondary,
  },
  failed: {
    color: theme.palette.error.dark,
  },
}));

type PageRowMetaProps = {
  page: ImportPage;
  ocr: OcrPageState | undefined;
  extracted: boolean;
};

/** File name, page number, failure or line count, and the status chip of one page-list row. */
export const PageRowMeta = ({ page, ocr, extracted }: PageRowMetaProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const status = resolveStatus(extracted, ocr);
  const failedKey = page.failed ? failedMessageKey(page.failed) : null;

  return (
    <>
      <div className={classes.meta}>
        <Typography variant="body2" noWrap title={page.fileName}>
          {page.fileName}
        </Typography>
        <Typography variant="caption" display="block" className={classes.muted}>
          {t('imports:viewer.page', { page: page.pageNumber })}
        </Typography>
        {page.failed && (
          <Typography variant="caption" display="block" className={classes.failed}>
            {failedKey ? t(failedKey) : page.failed}
          </Typography>
        )}
        {ocr?.status === 'done' && (
          <Typography variant="caption" display="block" className={classes.muted}>
            {t('imports:pages.lineCount', { total: ocr.lines.length })}
          </Typography>
        )}
      </div>
      <StatusChip label={t(status.labelKey)} tone={status.tone} />
    </>
  );
};
